'use client'

import { useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'

import Icon from '@/components/ui/icon'
import { cleanText, cn } from '@/lib/utils'
import { CategoryFilters, CategoryProperty, FilteredCategory, SupplierFilter } from '@/types/category.type'

import FilterItem from './FilterItem'

type FilterProps = {
  filters: CategoryFilters
  filtered: FilteredCategory['filtered']
  total: number
}

export default function Filter({ filters, filtered, total }: FilterProps) {
  const [isOpen, setIsOpen] = useState(true)
  const [suppliers, setSuppliers] = useState<SupplierFilter[]>(filters.suppliers)
  const [categoryProperties, setCategoryProperties] = useState<CategoryProperty[]>(filters.category_properties)
  const [selected, setSelected] = useState<number | null>(null)
  const [brand, setBrand] = useState<number[]>([])
  const [prid, setPrid] = useState<number[]>([])
  const [filteredData, setFilteredData] = useState<FilteredCategory['filtered']>(filtered) // các thuộc tính bị vô hiệu khi chọn bộ lọc
  const menuRef = useRef<HTMLDivElement>(null)
  const t = useTranslations('product')
  const searchParams = useSearchParams()

  useEffect(() => {
    setSuppliers(filters.suppliers)
    setCategoryProperties(filters.category_properties)
  }, [filters])

  useEffect(() => {
    setBrand((searchParams.get('brand') ?? '').split(',').filter(Boolean).map(Number))
    setPrid((searchParams.get('prid') ?? '').split(',').filter(Boolean).map(Number))
    setFilteredData(filtered)
  }, [searchParams, filtered])

  const isPridDisable = (id: number) => {
    if (filteredData.length === 0) return false
    const pridExist = filtered.find((f) => f.id === id)
    return pridExist ? false : true
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Nếu menu đang mở và click trúng cái gì đó NẰM NGOÀI menuRef thì gọi onClose()
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setSelected(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <div className="rounded">
      <div
        className="flex items-center justify-between bg-[#0070bb] px-[10px] py-1.5 text-base font-medium text-white cursor-pointer select-none rounded-tl rounded-tr"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-1">
          <Icon icon="tgic icon-filter-solid" />
          {t('filter_product')}
        </div>
        <Icon icon={isOpen ? 'tgic icon-chevron-up' : 'tgic icon-chevron-down'} />
      </div>
      {isOpen && (
        <ul className="bg-[#f7f9f9] w-full">
          {/* thương hiệu */}
          <li
            className="relative border-b border-b-bordercolor"
            onClick={(e) => {
              e.stopPropagation()
              setSelected(0)
            }}
          >
            <div
              className={cn(
                'px-2 py-2 text-sm border-l-2 border-l-transparent hover:border-l-[#bababa] hover:text-cyan-600 hover:bg-[#f2f2f2] cursor-pointer flex items-center justify-between',
                {
                  'bg-[#e2fde2] text-green-700 hover:bg-[#e2fde2] border-l-green-700 hover:border-l-gray-700': brand.length > 0,
                },
              )}
            >
              <span>{t('supplier')}</span>
              <Icon icon="tgic icon-chevron-right" />
            </div>

            {selected === 0 && <FilterItem ref={menuRef} type="brand" suppliers={suppliers} categoryProperty={null} onClose={() => setSelected(null)} totalInStock={filters.totalInStock} totalProduct={total} />}
          </li>

          {/* tiêu chí lọc */}
          {categoryProperties
            .filter((i) => cleanText(i.name).toLowerCase() !== cleanText('Thương Hiệu').toLowerCase())
            .map((cp, index) => (
              <li
                key={`category-property-${cp.id}`}
                className="relative border-b border-b-bordercolor"
                onClick={(e) => {
                  e.stopPropagation()
                  if (cp.childrens.every((c) => isPridDisable(c.id))) return
                  setSelected(index + 1)
                }}
              >
                <div
                  className={cn(
                    'px-2 py-2 text-sm border-l-2 border-l-transparent hover:border-l-[#bababa] hover:text-cyan-600 hover:bg-[#f2f2f2] cursor-pointer flex items-center justify-between',
                    {
                      'bg-[#e2fde2] text-green-700 hover:bg-[#e2fde2] border-l-green-700 hover:border-l-gray-700': cp.childrens.some((c) => prid.includes(c.id)),
                      'opacity-40 hover:text-inherit border-l-0 cursor-default': cp.childrens.every((c) => isPridDisable(c.id)),
                    },
                  )}
                >
                  <span>{cp.name}</span>
                  <Icon icon="tgic icon-chevron-right" />
                </div>

                {selected === index + 1 && (
                  <FilterItem ref={menuRef} type="prid" suppliers={null} categoryProperty={cp} prid={prid} setPrid={setPrid} onClose={() => setSelected(null)} isPridDisable={isPridDisable} totalInStock={filters.totalInStock} totalProduct={total} />
                )}
              </li>
            ))}

          {/* tồn kho */}
          <li
            className="relative border-b border-b-bordercolor"
            onClick={(e) => {
              e.stopPropagation()
              setSelected(categoryProperties.length + 1)
            }}
          >
            <div
              className={cn(
                'px-2 py-2 text-sm border-l-2 border-l-transparent hover:border-l-[#bababa] hover:text-cyan-600 hover:bg-[#f2f2f2] cursor-pointer flex items-center justify-between',
                {
                  'bg-[#e2fde2] text-green-700 hover:bg-[#e2fde2] border-l-green-700 hover:border-l-gray-700': searchParams.has('in_stock'),
                },
              )}
            >
              <span>{t('has_stock')}</span>
              <Icon icon="tgic icon-chevron-right" />
            </div>

            {selected === categoryProperties.length + 1 && <FilterItem ref={menuRef} type="stock" suppliers={null} categoryProperty={null} onClose={() => setSelected(null)} totalInStock={filters.totalInStock} totalProduct={total} />}
          </li>
        </ul>
      )}
    </div>
  )
}
